import { base64UrlToBytes, bytesToBase64Url } from "./base64url";

// Bridges go-webauthn's JSON options (every binary field base64url) and
// navigator.credentials, and serializes the resulting PublicKeyCredential
// the way the server parses it (cmd/core-platform/webauthn.go:77-82:
// "PublicKeyCredential.toJSON() verbatim").
//
// Server policy (internal/auth/webauthn.go:22-55): user verification is
// REQUIRED, attestation "none", resident keys discouraged — so a key
// without a PIN/biometric cannot be registered at all.

/** Shape go-webauthn emits for protocol.CredentialCreation / CredentialAssertion. */
export interface ServerCreationOptions {
  publicKey: {
    challenge: string;
    rp: { id?: string; name: string };
    user: { id: string; name: string; displayName: string };
    pubKeyCredParams: Array<{ type: "public-key"; alg: number }>;
    timeout?: number;
    excludeCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
    authenticatorSelection?: AuthenticatorSelectionCriteria;
    attestation?: AttestationConveyancePreference;
    extensions?: Record<string, unknown>;
  };
}

export interface ServerRequestOptions {
  publicKey: {
    challenge: string;
    timeout?: number;
    rpId?: string;
    allowCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
    userVerification?: UserVerificationRequirement;
    extensions?: Record<string, unknown>;
  };
}

export class WebAuthnUnsupportedError extends Error {
  constructor() {
    super("This browser does not support security keys or passkeys.");
    this.name = "WebAuthnUnsupportedError";
  }
}

export class WebAuthnCancelledError extends Error {
  constructor() {
    super("The security-key prompt was cancelled or timed out.");
    this.name = "WebAuthnCancelledError";
  }
}

export function webAuthnSupported(): boolean {
  return typeof window !== "undefined" && typeof window.PublicKeyCredential !== "undefined" && !!navigator.credentials;
}

function descriptor(d: { type: "public-key"; id: string; transports?: string[] }): PublicKeyCredentialDescriptor {
  return {
    type: d.type,
    id: base64UrlToBytes(d.id),
    ...(d.transports ? { transports: d.transports as AuthenticatorTransport[] } : {}),
  };
}

export function toCreationOptions(opts: ServerCreationOptions): PublicKeyCredentialCreationOptions {
  const pk = opts.publicKey;
  return {
    challenge: base64UrlToBytes(pk.challenge),
    rp: pk.rp,
    user: { id: base64UrlToBytes(pk.user.id), name: pk.user.name, displayName: pk.user.displayName },
    pubKeyCredParams: pk.pubKeyCredParams,
    ...(pk.timeout !== undefined ? { timeout: pk.timeout } : {}),
    ...(pk.excludeCredentials ? { excludeCredentials: pk.excludeCredentials.map(descriptor) } : {}),
    ...(pk.authenticatorSelection ? { authenticatorSelection: pk.authenticatorSelection } : {}),
    ...(pk.attestation ? { attestation: pk.attestation } : {}),
  };
}

export function toRequestOptions(opts: ServerRequestOptions): PublicKeyCredentialRequestOptions {
  const pk = opts.publicKey;
  return {
    challenge: base64UrlToBytes(pk.challenge),
    ...(pk.timeout !== undefined ? { timeout: pk.timeout } : {}),
    ...(pk.rpId ? { rpId: pk.rpId } : {}),
    ...(pk.allowCredentials ? { allowCredentials: pk.allowCredentials.map(descriptor) } : {}),
    ...(pk.userVerification ? { userVerification: pk.userVerification } : {}),
  };
}

/** JSON form of a PublicKeyCredential (WebAuthn L3 toJSON), built by hand for older browsers. */
export function credentialToJSON(cred: PublicKeyCredential): Record<string, unknown> {
  const base = {
    id: cred.id,
    rawId: bytesToBase64Url(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults?.() ?? {},
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
  };
  const r = cred.response;
  if ("attestationObject" in r) {
    const att = r as AuthenticatorAttestationResponse;
    return {
      ...base,
      response: {
        clientDataJSON: bytesToBase64Url(att.clientDataJSON),
        attestationObject: bytesToBase64Url(att.attestationObject),
        transports: typeof att.getTransports === "function" ? att.getTransports() : [],
      },
    };
  }
  const as = r as AuthenticatorAssertionResponse;
  return {
    ...base,
    response: {
      clientDataJSON: bytesToBase64Url(as.clientDataJSON),
      authenticatorData: bytesToBase64Url(as.authenticatorData),
      signature: bytesToBase64Url(as.signature),
      userHandle: as.userHandle ? bytesToBase64Url(as.userHandle) : undefined,
    },
  };
}

function mapDomError(err: unknown): never {
  if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "AbortError")) {
    throw new WebAuthnCancelledError();
  }
  throw err;
}

export async function createCredential(opts: ServerCreationOptions): Promise<Record<string, unknown>> {
  if (!webAuthnSupported()) throw new WebAuthnUnsupportedError();
  let cred: Credential | null;
  try {
    cred = await navigator.credentials.create({ publicKey: toCreationOptions(opts) });
  } catch (err) {
    mapDomError(err);
  }
  if (!cred) throw new WebAuthnCancelledError();
  return credentialToJSON(cred as PublicKeyCredential);
}

export async function getAssertion(opts: ServerRequestOptions): Promise<Record<string, unknown>> {
  if (!webAuthnSupported()) throw new WebAuthnUnsupportedError();
  let cred: Credential | null;
  try {
    cred = await navigator.credentials.get({ publicKey: toRequestOptions(opts) });
  } catch (err) {
    mapDomError(err);
  }
  if (!cred) throw new WebAuthnCancelledError();
  return credentialToJSON(cred as PublicKeyCredential);
}
